import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import Ikon from './ikon';
import { BELGE_KABUL, GORSEL_KABUL, boyut, uzanti, yerel, yetkiliFetch, type Detay, type Dosya, type DosyaTuru } from './ortak';
import { adresDenetle, sor } from '../../ui/Sor';

/**
 * MEDYA — ürün görselleri ızgarası + teknik çizim ve belgeler şeridi.
 *
 * Görseller anında yüklenir (Kaydet beklemez): Gözat, sürükle-bırak ya da
 * panodan yapıştır (Ctrl+V). İlk kutu ANA görseldir (teklifte ve listede o
 * görünür). Kutular sürüklenerek sıralanır; başa alınan görsel ana görsel olur.
 */
type Bildir = { hata: (m: string | null) => void; bilgi: (m: string | null) => void };

export function useDosyaIslem(urunId: number, yenile: () => Promise<unknown>, b: Bildir) {
  const [yuklenen, setYuklenen] = useState<null | { kind: DosyaTuru; sira: number; toplam: number }>(null);

  /** Dosyaları sırayla yükler (sunucu istek başına tek dosya alır). */
  const yukle = useCallback(async (kind: DosyaTuru, dosyalar: File[]) => {
    if (dosyalar.length === 0) return;
    b.hata(null); b.bilgi(null);
    let eklenen = 0;
    try {
      for (let n = 0; n < dosyalar.length; n++) {
        setYuklenen({ kind, sira: n + 1, toplam: dosyalar.length });
        const f = dosyalar[n];
        const fd = new FormData();
        // Panodan yapıştırılan görselin adı olmayabilir; uzantı türden türetilir.
        fd.append('dosya', f, f.name || `gorsel.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`);
        const res = await yetkiliFetch(`/api/urunler/${urunId}/dosya?kind=${kind}`, { method: 'POST', body: fd });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(`${f.name || 'Dosya'}: ${j.error ?? 'yüklenemedi'}`);
        eklenen += 1;
      }
      b.bilgi(eklenen === 1 ? 'Dosya eklendi' : `${eklenen} dosya eklendi`);
    } catch (e) {
      b.hata(e instanceof Error ? e.message : 'Dosya yüklenemedi');
    } finally {
      setYuklenen(null);
      await yenile();
    }
  }, [urunId, yenile, b]);

  const calistir = async (is: () => Promise<unknown>, tamam: string | null, olmadi: string) => {
    b.hata(null); b.bilgi(null);
    try { await is(); if (tamam) b.bilgi(tamam); await yenile(); }
    catch (e) { b.hata(e instanceof Error ? e.message : olmadi); await yenile(); }
  };

  const sil = (f: Dosya) => {
    if (!window.confirm(`"${f.filename}" silinsin mi? Bu işlem geri alınamaz.`)) return;
    void calistir(() => api(`/api/urunler/dosya/${f.id}`, { method: 'DELETE' }), 'Dosya silindi', 'Dosya silinemedi');
  };
  const birincilYap = (f: Dosya) =>
    calistir(() => api(`/api/urunler/${urunId}/dosya/${f.id}/birincil`, { method: 'POST' }), 'Ana görsel değişti', 'Ana görsel yapılamadı');
  /** Yeni sıra: ilk sıradaki görsel ana görsel değilse ana görsel de o olur. */
  const sirala = (ids: number[], anaYap: Dosya | null) => calistir(async () => {
    await api(`/api/urunler/${urunId}/dosya/sirala`, { method: 'POST', body: JSON.stringify({ ids }) });
    if (anaYap) await api(`/api/urunler/${urunId}/dosya/${anaYap.id}/birincil`, { method: 'POST' });
  }, 'Görsel sırası kaydedildi', 'Sıra kaydedilemedi');
  const notDegis = async (f: Dosya) => {
    const yeni = await sor({ baslik: 'Dosya notu', etiket: 'Not', ilk: f.note ?? '', not: 'Boş bırakırsanız not silinir.', azami: 300 });
    if (yeni == null) return;
    void calistir(() => api(`/api/urunler/dosya/${f.id}`, { method: 'PATCH', body: JSON.stringify({ note: yeni.trim() || null }) }), null, 'Not kaydedilemedi');
  };
  const disAdres = (adres: string | null) =>
    calistir(() => api(`/api/urunler/${urunId}`, { method: 'PATCH', body: JSON.stringify({ imageUrl: adres }) }),
      adres ? 'Görsel adresi kaydedildi' : 'Dış adresli görsel kaldırıldı', 'Görsel adresi kaydedilemedi');

  /** Jeton gerektiği için dosya fetch ile alınır; pdf/resim yeni sekmede, diğerleri indirilir. */
  const ac = async (f: Dosya, goster: boolean) => {
    b.hata(null);
    // Sekme tıklama anında açılmalı; indirme bittikten sonra açılırsa tarayıcı engeller.
    const sekme = goster ? window.open('', '_blank') : null;
    try {
      const res = await yetkiliFetch(`/api/urunler/dosya/${f.id}${goster ? '?goster=1' : ''}`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? 'Dosya açılamadı');
      const url = URL.createObjectURL(await res.blob());
      if (sekme) sekme.location.href = url;
      else { const a = document.createElement('a'); a.href = url; a.download = f.filename; a.click(); }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      sekme?.close();
      b.hata(e instanceof Error ? e.message : 'Dosya açılamadı');
    }
  };

  return { yuklenen, yukle, sil, birincilYap, sirala, notDegis, disAdres, ac };
}
type Islem = ReturnType<typeof useDosyaIslem>;

/* ─── Görsel ızgarası ──────────────────────────────────────────────────── */
export function MedyaIzgara({ d, yazabilir, islem }: { d: Detay; yazabilir: boolean; islem: Islem }) {
  const hepsi = d.dosyalar.filter((f) => f.kind === 'GORSEL');
  const ana = hepsi.find((g) => g.url === d.imageUrl) ?? null;
  // Galeriden gelmeyen eski görsel adresi (hızlı tanımda yapıştırılmış) kaybolmasın.
  const disAdres = d.imageUrl && !ana ? d.imageUrl : null;
  // Ana görsel her zaman ilk kutuda.
  const gorseller = ana ? [ana, ...hepsi.filter((g) => g.id !== ana.id)] : hepsi;

  const [uzerinde, setUzerinde] = useState(false);
  const [tasinan, setTasinan] = useState<number | null>(null);
  const [hedef, setHedef] = useState<number | null>(null);
  const [buyuk, setBuyuk] = useState<string | null>(null);
  const mesgul = islem.yuklenen?.kind === 'GORSEL' ? `${islem.yuklenen.sira} / ${islem.yuklenen.toplam} yükleniyor…` : null;
  const yukleRef = useRef(islem.yukle);
  yukleRef.current = islem.yukle;

  // Sayfada Ctrl+V: panodaki görsel hemen eklenir (yazı alanına yapıştırma hariç).
  useEffect(() => {
    if (!yazabilir) return;
    const yapistir = (e: ClipboardEvent) => {
      const h = e.target as HTMLElement | null;
      if (h && (h.tagName === 'INPUT' || h.tagName === 'TEXTAREA' || h.isContentEditable)) return;
      const fs = [...(e.clipboardData?.files ?? [])].filter((x) => x.type.startsWith('image/'));
      if (fs.length) { e.preventDefault(); void yukleRef.current('GORSEL', fs); }
    };
    document.addEventListener('paste', yapistir);
    return () => document.removeEventListener('paste', yapistir);
  }, [yazabilir]);

  useEffect(() => {
    if (!buyuk) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setBuyuk(null); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [buyuk]);

  const dosyaMi = (e: React.DragEvent) => [...e.dataTransfer.types].includes('Files');

  function birak(uzerineId: number) {
    const kaynak = tasinan;
    setTasinan(null); setHedef(null);
    if (kaynak == null || kaynak === uzerineId) return;
    const sira = gorseller.map((g) => g.id);
    const nereden = sira.indexOf(kaynak); const nereye = sira.indexOf(uzerineId);
    if (nereden < 0 || nereye < 0) return;
    sira.splice(nereye, 0, ...sira.splice(nereden, 1));
    const ilk = gorseller.find((g) => g.id === sira[0]) ?? null;
    // Dış adresli ana görsel varken ana görsele dokunulmaz.
    void islem.sirala(sira, !disAdres && ilk && ilk.id !== ana?.id ? ilk : null);
  }

  const bos = gorseller.length === 0 && !disAdres;

  return (
    <div
      className={`ccd-uk-medya${uzerinde ? ' ccd-uk-birak' : ''}`}
      onDragOver={yazabilir ? (e) => { if (dosyaMi(e)) { e.preventDefault(); setUzerinde(true); } } : undefined}
      onDragLeave={yazabilir ? () => setUzerinde(false) : undefined}
      onDrop={yazabilir ? (e) => {
        if (!dosyaMi(e)) return;
        e.preventDefault(); setUzerinde(false);
        void islem.yukle('GORSEL', [...e.dataTransfer.files].filter((x) => x.type.startsWith('image/')));
      } : undefined}
    >
      {bos && !yazabilir ? (
        <div className="ccd-uk-medya-bos"><Ikon ad="gorsel" boy={28} /><span>Bu ürüne henüz görsel eklenmedi.</span></div>
      ) : (
        <div className={`ccd-uk-izgara${bos ? ' ccd-uk-izgara-bos' : ''}`}>
          {disAdres && (
            <div className="ccd-uk-kare ccd-uk-kare-ana">
              <button type="button" className="ccd-uk-kare-resim" onClick={() => setBuyuk(disAdres)} aria-label="Ana görseli büyüt">
                <img src={disAdres} alt="" />
              </button>
              <span className="ccd-uk-kare-rozet">Ana görsel · dış adres</span>
              {yazabilir && (
                <div className="ccd-uk-kare-islem">
                  <button type="button" title="Dış adresli görseli kaldır" aria-label="Dış adresli görseli kaldır"
                          onClick={() => { if (window.confirm('Dış adresli ana görsel kaldırılsın mı?')) void islem.disAdres(null); }}>
                    <Ikon ad="cop" boy={16} />
                  </button>
                </div>
              )}
            </div>
          )}
          {gorseller.map((g, n) => {
            const anaMi = !disAdres && n === 0;
            return (
              <div
                key={g.id}
                className={`ccd-uk-kare${anaMi ? ' ccd-uk-kare-ana' : ''}${tasinan === g.id ? ' ccd-uk-tasinan' : ''}${hedef === g.id && tasinan !== g.id ? ' ccd-uk-hedef' : ''}`}
                draggable={yazabilir && gorseller.length > 1}
                onDragStart={(e) => { setTasinan(g.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(g.id)); }}
                onDragEnd={() => { setTasinan(null); setHedef(null); }}
                onDragOver={(e) => { if (tasinan != null) { e.preventDefault(); e.stopPropagation(); setHedef(g.id); } }}
                onDrop={(e) => { if (tasinan != null) { e.preventDefault(); e.stopPropagation(); birak(g.id); } }}
              >
                <button type="button" className="ccd-uk-kare-resim" onClick={() => setBuyuk(yerel(g.url))}
                        aria-label={`${g.filename} görselini büyüt`} title={g.filename}>
                  <img src={yerel(g.url)} alt="" loading="lazy" draggable={false} />
                </button>
                {anaMi && <span className="ccd-uk-kare-rozet">Ana görsel</span>}
                {yazabilir && (
                  <div className="ccd-uk-kare-islem">
                    {!anaMi && (
                      <button type="button" title="Ana görsel yap" aria-label={`${g.filename}: ana görsel yap`}
                              onClick={() => void islem.sirala([g.id, ...gorseller.filter((x) => x.id !== g.id).map((x) => x.id)], g)}>
                        <Ikon ad="yildiz" boy={16} />
                      </button>
                    )}
                    <button type="button" title="Sil" aria-label={`${g.filename}: sil`} onClick={() => islem.sil(g)}>
                      <Ikon ad="cop" boy={16} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {yazabilir && (
            <label className={`ccd-uk-kare ccd-uk-ekle${bos ? ' ccd-uk-ekle-genis' : ''}`} data-tur="gorsel-ekle">
              <input type="file" accept={GORSEL_KABUL} multiple hidden disabled={!!mesgul}
                     onChange={(e) => { const fs = [...(e.target.files ?? [])]; e.target.value = ''; void islem.yukle('GORSEL', fs); }} />
              {mesgul ? <span>{mesgul}</span> : (
                <>
                  <Ikon ad="arti" boy={20} />
                  <span>Görsel ekle</span>
                  {bos && <span className="ccd-uk-ekle-not">Gözat, buraya sürükleyin ya da kopyaladığınız görseli Ctrl+V ile yapıştırın</span>}
                </>
              )}
            </label>
          )}
        </div>
      )}
      {yazabilir && (
        <div className="ccd-uk-medya-alt">
          <span>
            {bos ? 'JPG, PNG, WEBP, GIF · en çok 8 MB.' : 'İlk kutu ana görseldir; teklifte ve listede o görünür. Sıralamak için kutuyu sürükleyin. JPG, PNG, WEBP, GIF · en çok 8 MB.'}
          </span>
          <button type="button" className="ccd-uk-metin-dugme" onClick={async () => {
            const adres = await sor({
              baslik: 'Görseli adresle ekle', aciklama: 'Ana görsel bu internet adresindeki resim olur; dosya panele yüklenmez.',
              etiket: 'Görsel adresi', tur: 'adres', ilk: disAdres ?? '', yerTutucu: 'https://…', zorunlu: true, azami: 1000, onay: 'Görseli ekle', denetle: adresDenetle,
            });
            if (adres != null) void islem.disAdres(adres);
          }}>Adresle ekle</button>
        </div>
      )}
      {buyuk && (
        <div className="ccd-uk-buyuk" role="dialog" aria-modal="true" aria-label="Görsel" onClick={() => setBuyuk(null)}>
          <img src={buyuk} alt="Ürün görseli" />
          <button type="button" className="ccd-uk-buyuk-kapat" aria-label="Kapat"><Ikon ad="carpi" boy={20} /></button>
        </div>
      )}
    </div>
  );
}

/* ─── Teknik çizim ve belgeler ─────────────────────────────────────────── */
export function Belgeler({ d, yazabilir, islem }: { d: Detay; yazabilir: boolean; islem: Islem }) {
  return (
    <>
      {([['TEKNIK', 'Teknik çizimler', 'Henüz teknik çizim yok.'], ['BELGE', 'Belgeler', 'Henüz belge yok.']] as const).map(([kind, baslik, bos]) => (
        <DosyaGrubu
          key={kind} baslik={baslik} bos={bos} yazabilir={yazabilir}
          dosyalar={d.dosyalar.filter((f) => f.kind === kind)}
          mesgul={islem.yuklenen?.kind === kind ? `${islem.yuklenen.sira} / ${islem.yuklenen.toplam} yükleniyor…` : null}
          yukle={(fs) => void islem.yukle(kind, fs)} islem={islem}
        />
      ))}
      {yazabilir && (
        <div className="ccd-field-hint">
          PDF, PNG, JPG, WEBP, DWG, DXF, XLSX ve DOCX yüklenebilir (en çok 25 MB). Dosyayı ilgili alana sürükleyip bırakabilirsiniz.
        </div>
      )}
    </>
  );
}

function DosyaGrubu({
  baslik, bos, dosyalar, yazabilir, mesgul, yukle, islem,
}: {
  baslik: string; bos: string; dosyalar: Dosya[]; yazabilir: boolean; mesgul: string | null;
  yukle: (dosyalar: File[]) => void; islem: Islem;
}) {
  const [uzerinde, setUzerinde] = useState(false);
  return (
    <div
      className={`ccd-uk-belge-grup${uzerinde ? ' ccd-uk-birak' : ''}`}
      onDragOver={yazabilir ? (e) => { e.preventDefault(); e.stopPropagation(); setUzerinde(true); } : undefined}
      onDragLeave={yazabilir ? () => setUzerinde(false) : undefined}
      onDrop={yazabilir ? (e) => { e.preventDefault(); e.stopPropagation(); setUzerinde(false); yukle([...e.dataTransfer.files]); } : undefined}
    >
      <div className="ccd-uk-belge-bas">
        <span className="ccd-uk-altbaslik">{baslik}</span>
        {yazabilir && (
          <label className="ccd-btn ccd-btn-sm ccd-sp-hap" style={{ cursor: mesgul ? 'default' : 'pointer' }}>
            {mesgul ?? 'Gözat…'}
            <input type="file" accept={BELGE_KABUL} multiple hidden disabled={!!mesgul}
                   onChange={(e) => { const fs = [...(e.target.files ?? [])]; e.target.value = ''; yukle(fs); }} />
          </label>
        )}
      </div>
      {dosyalar.length === 0 ? (
        <div className="ccd-meta">{bos}{yazabilir ? ' Gözat ile seçin ya da dosyayı buraya bırakın.' : ''}</div>
      ) : (
        dosyalar.map((f) => {
          const acilir = f.mime === 'application/pdf' || f.mime.startsWith('image/');
          return (
            <div className="ccd-uk-belge" key={f.id}>
              {f.mime.startsWith('image/')
                ? <Onizleme id={f.id} />
                : <div className="ccd-uk-belge-tur ccd-mono">{uzanti(f.filename)}</div>}
              <div className="ccd-uk-belge-ad">
                <div>{f.filename}</div>
                <div className="ccd-meta">{uzanti(f.filename)} · {boyut(f.size)}{f.note ? ` · ${f.note}` : ''}</div>
              </div>
              <div className="ccd-uk-belge-islem">
                {acilir && <button type="button" className="ccd-btn ccd-btn-sm" onClick={() => void islem.ac(f, true)}>Aç</button>}
                <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => void islem.ac(f, false)}>İndir</button>
                {yazabilir && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => islem.notDegis(f)}>Not</button>}
                {yazabilir && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-uk-tehlike" onClick={() => islem.sil(f)}>Sil</button>}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/** Resim türündeki teknik çizimin küçük önizlemesi — jeton gerektiği için blob ile. */
function Onizleme({ id }: { id: number }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let iptal = false, blobUrl: string | null = null;
    yetkiliFetch(`/api/urunler/dosya/${id}?goster=1`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b && !iptal) { blobUrl = URL.createObjectURL(b); setUrl(blobUrl); } })
      .catch(() => undefined);
    return () => { iptal = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [id]);
  return url
    ? <img className="ccd-uk-belge-onizleme" src={url} alt="" />
    : <div className="ccd-uk-belge-tur ccd-mono">…</div>;
}
